import { useEffect, useState } from "react";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarIcon, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Stethoscope, FileSearch, Users, Activity, Video, AlertTriangle, HeartPulse } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "@/components/ui/sonner";
import { createBooking, getPaymentConfig } from "@/lib/api";
import { PACKAGES, inr } from "@/lib/packages";
import PayButton from "@/components/PayButton";
import { StepDots, inputCls } from "@/components/BookingModal";

const STEPS = ["Who it's for", "Your details", "Payment"];
const CONSULT = PACKAGES.consultation;

const SUITABLE_FOR = [
  { icon: Stethoscope, text: "Existing liver problems — Fatty Liver, Hepatitis B, Hepatitis C, Liver Fibrosis, Liver Cirrhosis, Alcoholic liver problems" },
  { icon: FileSearch, text: "Existing abnormal liver reports" },
  { icon: Users, text: "Second opinions" },
  { icon: Activity, text: "Those requiring liver transplant" },
  { icon: Video, text: "Not able to visit in person" },
  { icon: AlertTriangle, text: "SuperAge assessment result — Red category", red: true },
  { icon: HeartPulse, text: "Want to enroll in physician-led Liver Recovery / Reset programs" },
];

// 3-card online consultation flow: who it's for → your details → payment/callback.
export const ConsultFlow = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", concerns: "" });
  const [date, setDate] = useState();
  const [calOpen, setCalOpen] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // 'paid' | 'callback'
  const [paymentOn, setPaymentOn] = useState(true);

  useEffect(() => {
    getPaymentConfig().then((c) => setPaymentOn(!!c?.enabled)).catch(() => setPaymentOn(false));
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validBasics = () => {
    if (!form.name.trim()) {
      toast.error("Please fill in your name.");
      return false;
    }
    if (!/^\d{10}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return false;
    }
    return true;
  };

  const bookingPayload = () => ({
    name: form.name,
    email: form.email,
    phone: form.phone,
    concerns: form.concerns,
    program: "online-consultation",
    package: "consultation",
    preferred_date: date ? format(date, "yyyy-MM-dd") : null,
  });

  // Pay path: create the booking, then hand its id to Razorpay.
  const resolvePayLead = async () => {
    if (!validBasics()) return null;
    try {
      const booking = await createBooking(bookingPayload());
      setBookingId(booking.id);
      return { name: form.name, email: form.email, phone: form.phone, booking_id: booking.id };
    } catch {
      toast.error("Could not create your booking. Please try again.");
      return null;
    }
  };

  // Free path: request a callback without paying.
  const requestCallback = async () => {
    setLoading(true);
    try {
      if (!bookingId) {
        const booking = await createBooking(bookingPayload());
        setBookingId(booking.id);
      }
      setDone("callback");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="consult-flow">
      <h2 className="font-display text-xl font-medium text-sa-forest">
        {done
          ? (done === "paid" ? "Consultation reserved." : "Request received.")
          : step === 1
            ? "Online Liver Consultation"
            : step === 2
              ? "Your details"
              : "Reserve your consultation"}
      </h2>
      <p className="mt-1 text-sm text-sa-muted">
        {done
          ? "You're all set."
          : step === 1
            ? "Talk to a liver specialist from home — step 1 of 3."
            : step === 2
              ? "Takes under a minute — step 2 of 3."
              : "Pay online, or let us call you — step 3 of 3."}
      </p>

      {!done && <div className="mt-3"><StepDots step={step} labels={STEPS} /></div>}

      <div className="mt-4">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center py-6 text-center"
              data-testid="consult-success"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sa-copper/15 text-sa-copper">
                <CheckCircle2 className="h-8 w-8" />
              </span>
              <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-sa-muted">
                {done === "paid"
                  ? `Thank you, ${form.name.split(" ")[0]}. Your consultation is reserved and paid — our care team will call to schedule your video call with the specialist.`
                  : `Thank you, ${form.name.split(" ")[0]}. Our care team will call you shortly to schedule your online consultation.`}
              </p>
            </motion.div>
          ) : step === 1 ? (
            <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="consult-step-1">
              <div className="rounded-2xl border border-sa-beige bg-sa-bg/60 p-5 text-center">
                <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-sa-copper">{CONSULT.fullName}</p>
                <p className="mt-1 font-display text-4xl font-medium text-sa-forest" data-testid="consult-price">{inr(CONSULT.price)}</p>
                <p className="mt-1.5 text-[12px] text-sa-muted">Video call with a liver specialist · from anywhere in India</p>
              </div>
              <p className="mt-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-sa-copper">Who is this for</p>
              <div className="mt-2 space-y-2.5" data-testid="consult-suitable">
                {SUITABLE_FOR.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.text}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + i * 0.05, duration: 0.35 }}
                      className={`flex items-start gap-3 rounded-2xl border p-3.5 ${item.red ? "border-risk-red/25 bg-risk-red/5" : "border-sa-beige bg-white"}`}
                      data-testid={`consult-suitable-${i}`}
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm ${item.red ? "bg-risk-red/10 text-risk-red" : "bg-sa-bg text-sa-copper"}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <p className="text-[13.5px] leading-relaxed text-sa-forest">{item.text}</p>
                    </motion.div>
                  );
                })}
              </div>
              <button
                onClick={() => setStep(2)}
                data-testid="consult-continue"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-sa-forest px-7 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] hover:bg-sa-forest-soft"
              >
                Continue to booking <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          ) : step === 2 ? (
            <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-3" data-testid="consult-step-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-sa-forest">Full name</label>
                <input value={form.name} onChange={update("name")} placeholder="Your name" data-testid="consult-name" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="mb-1 block text-sm font-medium text-sa-forest">Email <span className="font-normal text-sa-muted">(optional)</span></label>
                  <input type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="consult-email" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-sa-forest">Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    data-testid="consult-phone"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-sa-forest">Preferred date (optional)</label>
                <button
                  type="button"
                  onClick={() => setCalOpen((v) => !v)}
                  data-testid="consult-date"
                  className="flex w-full items-center justify-between rounded-xl border border-sa-beige bg-sa-bg px-4 py-2.5 text-left text-sa-forest outline-none transition-colors focus:border-sa-copper"
                >
                  <span className={date ? "" : "text-sa-muted"}>{date ? format(date, "PPP") : "Pick a date"}</span>
                  <CalendarIcon className="h-4 w-4 text-sa-copper" />
                </button>
                <AnimatePresence initial={false}>
                  {calOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 flex justify-center rounded-xl border border-sa-beige bg-sa-bg p-2" data-testid="consult-date-calendar">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => { setDate(d); setCalOpen(false); }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-sa-forest">Special concerns <span className="font-normal text-sa-muted">(optional)</span></label>
                <textarea
                  value={form.concerns}
                  onChange={update("concerns")}
                  rows={3}
                  placeholder="Anything specific you'd like the doctor to know — reports, symptoms, current medicines…"
                  data-testid="consult-concerns"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                onClick={() => validBasics() && setStep(3)}
                data-testid="consult-next"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-sa-forest px-7 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] hover:bg-sa-forest-soft"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setStep(1)} data-testid="consult-back" className="flex items-center gap-1 text-[13px] text-sa-muted transition-colors hover:text-sa-forest">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </motion.div>
          ) : (
            <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-4" data-testid="consult-step-3">
              <div className="rounded-2xl border border-sa-beige bg-sa-bg/60 p-5 text-center">
                <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-sa-copper">{CONSULT.fullName}</p>
                <p className="mt-1 font-display text-4xl font-medium text-sa-forest" data-testid="consult-pay-price">{inr(CONSULT.price)}</p>
                <p className="mt-1.5 text-[12px] text-sa-muted">Video call with a liver specialist · report review included</p>
              </div>
              {paymentOn ? (
                <PayButton
                  variant="copper"
                  showPromo={true}
                  tier="consultation"
                  className="[&>div]:flex [&>div]:justify-center"
                  resolveLead={resolvePayLead}
                  onPaid={() => setDone("paid")}
                />
              ) : (
                <p className="text-center text-sm text-sa-muted">Online payment is coming soon.</p>
              )}
              <button
                onClick={requestCallback}
                disabled={loading}
                data-testid="consult-callback"
                className="mx-auto flex items-center gap-1.5 text-sm font-medium text-sa-muted underline-offset-4 transition-colors hover:text-sa-forest hover:underline disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Request callback instead
              </button>
              <button onClick={() => setStep(2)} data-testid="consult-back-2" className="flex items-center gap-1 text-[13px] text-sa-muted transition-colors hover:text-sa-forest">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ConsultFlow;
